import { defaultCoverDoc, type CoverBackground, type CoverDoc } from 'fym-shared/src/music24/coverDoc';
import { COVER_AI_RUN_SIZE, canUseCoverAi } from 'fym-shared/src/music24/coverEditorPlans';
import type { Music24PlanTier } from 'fym-shared/src/music24/music24Plans';
import { themeColours, type ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import type { CoverAiQuota, CoverBankBackground, CoverWelcomeRun } from '../../../redux/api/fymAPI.types';
import { hashText } from '../render/seededRandom';

/**
 * The bank picture of a first version: one that fits the release theme of the song, else any
 * picture. The pick depends only on `seed` (the song), so two opens of the same song agree. Null
 * for an empty bank. Pure.
 */
export function pickBankBackground(
  backgrounds: readonly CoverBankBackground[],
  themeId: ReleaseThemeId,
  seed: string
): CoverBankBackground | null {
  const fitting = backgrounds.filter((background) => background.themeIds.includes(themeId));
  const pool = fitting.length > 0 ? fitting : backgrounds;
  return pool[hashText(seed) % Math.max(1, pool.length)] ?? null;
}

/**
 * True when the workspace can start one AI run now: a plan with AI and a full run left, or the
 * welcome run of a trial workspace (on us; the server checks it again). Pure.
 */
export function canStartCoverAiRun(
  plan: Music24PlanTier,
  quota: Pick<CoverAiQuota, 'used' | 'limit'>,
  welcomeRun?: Pick<CoverWelcomeRun, 'available'>
): boolean {
  if (welcomeRun?.available === true) {
    return true;
  }
  return canUseCoverAi(plan) && quota.limit - quota.used >= COVER_AI_RUN_SIZE;
}

/**
 * True when the first open of the song starts with the theme step (owner request 2026-10-09): a
 * trial workspace with its welcome run, on a song with no version yet. A song that already has a
 * version opens the editor; its AI tab still offers the welcome run. Pure.
 */
export function needsCoverThemeStep(input: {
  designCount: number;
  welcomeRun?: Pick<CoverWelcomeRun, 'available'>;
}): boolean {
  return input.designCount === 0 && input.welcomeRun?.available === true;
}

export interface FirstOpenInput {
  catalogItemUuid: string;
  song: { title: string; artistName: string; themeId: ReleaseThemeId; palette: readonly string[] };
  backgrounds: readonly CoverBankBackground[];
  plan: Music24PlanTier;
  quota: Pick<CoverAiQuota, 'used' | 'limit'>;
  /** The welcome run of the workspace; absent: none. */
  welcomeRun?: Pick<CoverWelcomeRun, 'available'>;
  /** Makes a bank address absolute. */
  toUrl: (url: string) => string;
}

export interface FirstOpenPlan {
  /** The first version: the default layout on a bank picture (or on a theme gradient with no bank). */
  doc: CoverDoc;
  /**
   * True only with the welcome run of the trial (4 pictures on us, no quota). A paid plan starts
   * its AI run with the button: an open of the page never spends AI pictures (QA 2026-10-24 B15).
   */
  startAiRun: boolean;
}

/**
 * The first version of a song with no design (owner decision D4: the editor never opens empty).
 * Every plan gets the default layout on a bank picture that fits the theme of the song. The welcome
 * run of the trial also starts one AI run; its first picture replaces the bank picture while the
 * user has not changed anything. A paid plan starts its run with the button. With an empty bank the
 * ground is a gradient of the theme colours. Pure.
 */
export function firstOpenPlan(input: FirstOpenInput): FirstOpenPlan {
  const { song } = input;
  const picked = pickBankBackground(input.backgrounds, song.themeId, input.catalogItemUuid);
  const colours = themeColours(song.themeId, song.palette);
  const background: CoverBackground =
    picked === null
      ? { kind: 'gradient', from: colours.surface, to: colours.background, angleDeg: 160 }
      : {
          kind: 'picture',
          source: { kind: 'bank', backgroundUuid: picked.uuid },
          url: input.toUrl(picked.url),
          focusX: 0.5,
          focusY: 0.5,
          zoom: 1,
        };
  const palette = picked !== null && picked.palette.length > 0 ? picked.palette : [...song.palette];
  return {
    doc: defaultCoverDoc({
      themeId: song.themeId,
      palette: palette.length > 0 ? palette : [colours.background, colours.surface, colours.accent, colours.text],
      title: song.title,
      artist: song.artistName,
      background,
    }),
    startAiRun: input.welcomeRun?.available === true,
  };
}
